import { observer } from 'mobx-react'
import type { CSSProperties } from 'react'
import { useContext, useEffect, useRef, useState } from 'react'
import { Button, Header, LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { API_URL, EASYSIGN_SERVICE_PATH, ONBOARDING_SERVICE_PATH } from '@/constants/apiUrl'
import { devLog } from '@/methods/devLog'
import axios from 'axios'
import { forceRedirect } from '@/methods/forceRedirect'
import { rootStore } from '@/store/Root.store'
import { cx } from '@/utils/cx'

export const EasySignSignaturePage = observer(() => {
  const buttonRef = useRef(null)

  const store = useContext(StoreContext)
  const { easySignSignaturePage: trans } = store.TranslationsState.translationsNew
  const { screenSize, isMobile } = store.AppState

  // Mobile utilities
  const [mobileContainerHeight, setMobileContainerHeight] = useState('')

  const [btnLoading, setBtnLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
    fullName: '',
  })

  const goBackHandler = () => {
    rootStore.ScenarioState.prevPage()
  }

  const getUserData = async () => {
    const { data } = await axios.get<{
      firstName?: string
      lastName?: string
      fullName?: string
    }>(`${API_URL}/${ONBOARDING_SERVICE_PATH}/user-data`, {
      withCredentials: true,
    })
    setUser({
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      fullName: data.fullName || '',
    })
    setIsLoading(false)
  }

  const handleProceed = async () => {
    if (btnLoading) return

    try {
      setBtnLoading(true)

      await axiosInstance.post(
        `${API_URL}/${EASYSIGN_SERVICE_PATH}/sign-document`,
        {},
        {
          withCredentials: true,
        }
      )

      forceRedirect(`${API_URL}/${EASYSIGN_SERVICE_PATH}/complete`)
    } catch (e) {
      devLog(e)
      setBtnLoading(false)
    }
  }

  useEffect(() => {
    if (screenSize.width && screenSize.width <= 980) {
      setMobileContainerHeight(`calc(${window.innerHeight - 250}px)`)
    }
  }, [screenSize.width])

  useEffect(() => {
    void getUserData()
  }, [])

  if (isLoading) {
    return (
      <div className="page-container mobile-m-10">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="page-container mobile-m-0 mobile-p-140-20-20">
      <button
        className="generic__back-button generic__back-button--absolute"
        onClick={goBackHandler}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14.7205 23.2794C15.0589 23.6178 15.6077 23.6178 15.9461 23.2794C16.2846 22.9409 16.2846 22.3922 15.9461 22.0537L10.759 16.8666L23.3333 16.8666C23.8119 16.8666 24.1999 16.4785 24.1999 15.9999C24.1999 15.5212 23.8119 15.1332 23.3333 15.1332L10.759 15.1332L15.9461 9.94605C16.2846 9.60759 16.2846 9.05885 15.9461 8.72039C15.6077 8.38194 15.0589 8.38194 14.7205 8.72039L8.05383 15.3871C7.71537 15.7255 7.71537 16.2743 8.05383 16.6127L14.7205 23.2794Z"
            fill="black"
          />
        </svg>

        {trans.back}
      </button>
      <div
        className="layout__content-container-with-scroll"
        style={
          isMobile
            ? ({ '--ccws-mobile-height': mobileContainerHeight } as CSSProperties)
            : undefined
        }
      >
        <Header className="no-margin">{trans.header}</Header>

        <p className="generic__paragraph-centered">{trans.pressSignDocument}</p>

        <div className="generic__label">{trans.yourSignature}</div>

        <div className="generic__easy-sign__signature-wrapper">
          <div className="generic__easy-sign__signature">
            {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.fullName}
          </div>
        </div>
      </div>

      <div className="layout__content-container-bottom-fixed">
        <Button onClick={handleProceed} tabIndex={0} className="button--proceed" ref={buttonRef}>
          {btnLoading && <LoadingSpinner inButton />}
          <span
            className={cx('generic__button-text', btnLoading && 'generic__button-text--loading')}
          >
            {trans.proceed}
          </span>
        </Button>
      </div>
    </div>
  )
})
